<template>
  <div class="web-cashier-page">
    <main v-if="!qrPayData" class="web-main">
      <section class="merchant-card">
        <div class="merchant-row">
          <div class="merchant-avatar">
            <img :src="merchantIcon" alt="" />
          </div>
          <div class="merchant-meta">
            <div class="pay-title">{{ payTitle }}</div>
            <div class="merchant-name">{{ merchantDisplayName }}</div>
          </div>
        </div>

        <div class="amount-box">
          <span class="currency">&yen;</span>
          <div class="amount-text" :class="{ empty: !displayAmount }">
            {{ displayAmount || (isProductQrc ? '输入金额匹配商品' : '请输入金额') }}
          </div>
        </div>

        <div v-if="showOrderSummary" class="order-summary">
          <div v-if="orderSubject" class="order-row">
            <span class="order-label">商品名称</span>
            <span class="order-value">{{ orderSubject }}</span>
          </div>
          <div v-if="orderBody" class="order-row">
            <span class="order-label">商品说明</span>
            <span class="order-value">{{ orderBody }}</span>
          </div>
          <div v-if="payOrderInfo.outOrderNo" class="order-row">
            <span class="order-label">外部订单号</span>
            <span class="order-value">{{ payOrderInfo.outOrderNo }}</span>
          </div>
        </div>

        <div v-if="availableWays.length" class="pay-way-list">
          <button
            v-for="item in availableWays"
            :key="item.brand"
            type="button"
            :class="['pay-brand-button', item.brand, { active: selectedBrand === item.brand }]"
            @click="selectPayWay(item)"
          >
            <span class="brand-logo-wrap">
              <img class="brand-logo" :src="item.icon" :alt="item.label" />
            </span>
            <span class="brand-label">{{ item.label }}</span>
          </button>
        </div>
      </section>

      <section v-if="isProductQrc" class="product-list">
        <button
          v-for="item in matchedProducts"
          :key="item.productId"
          type="button"
          class="product-item"
          :class="{ active: item.productId === selectedProductId, exact: item.exactMatch }"
          @click="selectProduct(item)"
        >
          <div class="product-cover">
            <img :src="item.imageUrl || defaultProductImage" alt="" />
          </div>
          <div class="product-body">
            <div class="product-name">
              <span>{{ item.productName }}</span>
              <i v-if="item.customAmount" class="custom">自定义金额</i>
              <i v-else-if="item.exactMatch">精准匹配</i>
            </div>
            <div class="product-meta">
              <span>{{ item.customAmount ? `按输入金额 ${displayAmount}` : formatMoney(item.amount) }}</span>
              <template v-if="!item.customAmount">
                <span v-if="item.diffAmount > 0">高于输入 {{ formatMoney(item.diffAmount) }}</span>
                <span v-else-if="item.diffAmount < 0">低于输入 {{ formatMoney(Math.abs(item.diffAmount)) }}</span>
                <span v-else>金额一致</span>
              </template>
            </div>
          </div>
        </button>
        <div v-if="displayAmount && !matchedProducts.length && !matchLoading" class="empty-products">
          未匹配到可售商品
        </div>
      </section>
    </main>

    <section v-if="qrPayData" class="qr-result">
      <div class="qr-card">
        <h1>请扫码付款</h1>
        <img v-if="qrPayData.codeImgUrl || qrPayData.payData" :src="qrPayData.codeImgUrl || qrPayData.payData" alt="" />
        <p>{{ qrPayText }}</p>
      </div>
    </section>

    <section v-if="!qrPayData && !isFixedAmount" class="number-pad">
      <div class="key-grid">
        <button
          v-for="key in amountKeys"
          :key="key"
          type="button"
          class="num-key"
          @click="appendAmountKey(key)"
        >
          {{ key }}
        </button>
        <button type="button" class="num-key muted" @click="deleteAmountKey">⌫</button>
        <button type="button" class="num-key" @click="appendAmountKey('0')">0</button>
        <button type="button" class="num-key" @click="appendAmountKey('.')">.</button>
      </div>
      <button class="pay-button" type="button" :disabled="!canPay || paying || !selectedWayCode" @click="pay">
        {{ paying ? '处理中' : '付款' }}
      </button>
    </section>

    <section v-if="!qrPayData && isFixedAmount" class="fixed-pay-bar">
      <button class="pay-button" type="button" :disabled="!canPay || paying || !selectedWayCode" @click="pay">
        {{ paying ? '处理中' : '付款' }}
      </button>
    </section>
  </div>
</template>

<script>
import { getPayPackage, getPayOrderInfo, getProductMatch, getProductPayPackage } from '@/api/api'
import config from '@/config'
import { finishSuccess } from '@/utils/closePage'
import wayCodeUtils from '@/utils/wayCode'
import defaultProductImage from '../assets/images/default-product.jpg'
import alipayLogoUrl from '../assets/payBrand/alipay.svg'
import wechatPayLogoUrl from '../assets/payBrand/wechat-pay.svg'
import yunshanfuLogoUrl from '../assets/payBrand/yunshanfu.svg'

export default {
  data() {
    return {
      merchantName: '飞付通商户',
      amount: '',
      payOrderInfo: {},
      isProductQrc: false,
      availableWays: [],
      selectedWayCode: '',
      selectedBrand: '',
      paying: false,
      qrPayData: null,
      merchantIcon: require('../assets/icon/merchant.svg'),
      defaultProductImage,
      matchedProducts: [],
      selectedProductId: '',
      selectedProduct: null,
      matchLoading: false,
      matchTimer: null,
      matchSeq: 0,
      amountKeys: ['1', '2', '3', '4', '5', '6', '7', '8', '9']
    }
  },

  computed: {
    merchantDisplayName() {
      return this.payOrderInfo.mchName || this.merchantName
    },

    isFixedAmount() {
      return !!this.payOrderInfo.amount
    },

    isOpenapiOrder() {
      return this.payOrderInfo.openapiOrder === true
    },

    payTitle() {
      if (this.isProductQrc) {
        return '商品收款'
      }
      return this.isOpenapiOrder ? '订单收款' : '向商户付款'
    },

    orderSubject() {
      return this.payOrderInfo.subject || ''
    },

    orderBody() {
      return this.payOrderInfo.body || ''
    },

    showOrderSummary() {
      return this.isOpenapiOrder && (this.orderSubject || this.orderBody || this.payOrderInfo.outOrderNo)
    },

    displayAmount() {
      return this.amount ? String(this.amount) : ''
    },

    canPay() {
      if (this.isProductQrc) {
        return !!this.selectedProductId && Number(this.amount) > 0
      }
      return !isNaN(this.amount) && Number(this.amount) > 0
    },

    qrPayText() {
      if (this.selectedWayCode === config.payWay.WXPAY_NATIVE.wayCode) {
        return '请使用微信扫码完成付款'
      }
      if ([
        config.payWay.ALIPAY_QR.wayCode,
        config.payWay.ALIPAY_OC.wayCode
      ].indexOf(this.selectedWayCode) >= 0) {
        return '请使用支付宝扫码完成付款'
      }
      return '请扫码完成付款'
    }
  },

  mounted() {
    document.title = '向商户付款'
    if (wayCodeUtils.getPayWay() && !(this.$route.query && this.$route.query.forceWeb)) {
      this.$router.replace({ name: 'Hub', params: { jeepayToken: this.$route.params.jeepayToken } })
      return
    }
    this.setPayOrderInfo()
  },

  beforeDestroy() {
    if (this.matchTimer) {
      clearTimeout(this.matchTimer)
    }
  },

  methods: {
    setPayOrderInfo() {
      getPayOrderInfo('', false).then(res => {
        this.payOrderInfo = res
        this.isProductQrc = res.productQrc === true

        this.availableWays = this.buildAvailableWays(Array.isArray(res.supportWayCodes) ? res.supportWayCodes : [])
        if (!this.availableWays.length) {
          this.$router.replace({ name: config.errorPageRouteName, params: { errInfo: '当前商户暂未开通网页支付，请使用微信或支付宝打开' } })
          return
        }

        this.selectPayWay(this.availableWays[0], false)
        if (res.amount) {
          this.amount = this.formatAmount(res.amount)
          if (this.isProductQrc) {
            this.scheduleMatch()
          } else if (!this.isOpenapiOrder) {
            this.pay()
          }
        }
      }).catch(res => {
        this.$router.replace({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      })
    },

    buildAvailableWays(supportWayCodes) {
      const mobile = wayCodeUtils.isMobile()
      const has = wayCode => supportWayCodes.indexOf(wayCode) >= 0
      const pick = wayCodes => wayCodes.find(has) || ''
      const ways = []

      const wxWayCode = mobile
        ? pick([config.payWay.WXPAY_H5.wayCode])
        : pick([config.payWay.WXPAY_NATIVE.wayCode])
      if (wxWayCode) {
        ways.push({ brand: 'wechat', label: '微信支付', icon: wechatPayLogoUrl, wayCode: wxWayCode })
      }

      const aliWayCode = mobile
        ? pick([config.payWay.ALIPAY_WAP.wayCode])
        : pick([config.payWay.ALIPAY_PC.wayCode, config.payWay.ALIPAY_QR.wayCode, config.payWay.ALIPAY_OC.wayCode])
      if (aliWayCode) {
        ways.push({ brand: 'alipay', label: '支付宝', icon: alipayLogoUrl, wayCode: aliWayCode })
      }

      const ysfWayCode = mobile
        ? pick(['UPACP_WAP'])
        : pick(['UPACP_PC', 'UPACP_QR'])
      if (ysfWayCode) {
        ways.push({ brand: 'unionpay', label: '云闪付', icon: yunshanfuLogoUrl, wayCode: ysfWayCode })
      }

      const exclusiveWayCode = pick([config.payWay.EXCLUSIVE.wayCode])
      if (exclusiveWayCode) {
        ways.push({ brand: 'exclusive', label: '专属通道', icon: this.merchantIcon, wayCode: exclusiveWayCode })
      }

      const onlyWay = this.$route.query && this.$route.query.onlyWay
      if (onlyWay === 'exclusive') {
        return ways.filter(item => item.wayCode === config.payWay.EXCLUSIVE.wayCode)
      }

      return ways
    },

    selectPayWay(item, feedback = true) {
      if (!item) return
      if (feedback) {
        this.tapFeedback()
      }
      this.selectedBrand = item.brand
      this.selectedWayCode = item.wayCode
    },

    formatAmount(amountFen) {
      const amount = Number(amountFen || 0) / 100
      return amount.toFixed(2).replace(/\.?0+$/, '')
    },

    formatMoney(amountFen) {
      const amount = Number(amountFen || 0) / 100
      return amount.toFixed(2)
    },

    tapFeedback(duration = 12) {
      if (window.navigator && typeof window.navigator.vibrate === 'function') {
        window.navigator.vibrate(duration)
      }
    },

    appendAmountKey(key) {
      this.tapFeedback()
      let value = this.amount ? String(this.amount) : ''

      if (key === '.') {
        if (value.indexOf('.') >= 0) return
        this.amount = value ? value + '.' : '0.'
        this.scheduleMatchIfNeed()
        return
      }

      if (value.indexOf('.') >= 0 && value.split('.')[1].length >= 2) return

      if (value === '0') {
        this.amount = key
        this.scheduleMatchIfNeed()
        return
      }

      if (value.replace('.', '').length >= 10) return

      this.amount = value + key
      this.scheduleMatchIfNeed()
    },

    deleteAmountKey() {
      this.tapFeedback()
      if (!this.amount) return
      this.amount = String(this.amount).slice(0, -1)
      this.scheduleMatchIfNeed()
    },

    scheduleMatchIfNeed() {
      if (this.isProductQrc) {
        this.scheduleMatch()
      }
    },

    scheduleMatch() {
      if (this.matchTimer) {
        clearTimeout(this.matchTimer)
      }
      this.matchTimer = setTimeout(() => {
        this.refreshMatch()
      }, 220)
    },

    refreshMatch() {
      if (!Number(this.amount) || Number(this.amount) <= 0) {
        this.matchedProducts = []
        this.selectedProductId = ''
        this.selectedProduct = null
        return
      }

      this.matchLoading = true
      const amountSnapshot = this.amount
      const seq = ++this.matchSeq
      getProductMatch(this.amount).then(res => {
        if (seq !== this.matchSeq || amountSnapshot !== this.amount) {
          return
        }
        const payload = res.data || res
        const products = Array.isArray(payload.products) ? payload.products : []
        this.matchedProducts = products
        if (!products.length) {
          this.selectedProductId = ''
          this.selectedProduct = null
          return
        }

        const exact = products.find(item => item.exactMatch)
        this.selectProduct(exact || products[0], false)
      }).catch(() => {
        this.matchedProducts = []
        this.selectedProductId = ''
        this.selectedProduct = null
      }).finally(() => {
        this.matchLoading = false
      })
    },

    selectProduct(item, updateAmount = true) {
      if (!item) return
      this.selectedProductId = item.productId
      this.selectedProduct = item
      if (updateAmount && !item.customAmount) {
        this.amount = this.formatAmount(item.amount)
      }
    },

    pay() {
      this.tapFeedback(18)
      if (!this.canPay || !this.selectedWayCode || this.paying) return

      this.paying = true
      const extraData = this.needCodeImg(this.selectedWayCode) ? { payDataType: 'codeImgUrl' } : {}
      const payRequest = this.isProductQrc
        ? getProductPayPackage(this.selectedProduct && !this.selectedProduct.customAmount ? this.formatMoney(this.selectedProduct.amount) : this.amount, this.selectedProductId, this.selectedWayCode, extraData)
        : getPayPackage(this.amount, this.selectedWayCode, extraData)

      payRequest.then(res => {
        this.paying = false
        if (res.code !== 0 && res.code !== '0') {
          alert(res.msg)
          return
        }
        const payData = res.data || {}
        if (Number(payData.orderState) === 2) {
          this.handlePaySuccess()
          return
        }
        if (Number(payData.orderState) !== 1) {
          alert(payData.errMsg || '订单状态异常')
          return
        }
        this.handlePayData(payData)
      }).catch(res => {
        this.paying = false
        alert(res.msg || '创建支付订单失败')
      })
    },

    needCodeImg(wayCode) {
      return [
        config.payWay.WXPAY_NATIVE.wayCode,
        config.payWay.ALIPAY_QR.wayCode,
        config.payWay.ALIPAY_OC.wayCode,
        'UPACP_QR'
      ].indexOf(wayCode) >= 0
    },

    handlePayData(payData) {
      if (payData.payDataType === 'none') {
        this.handlePaySuccess()
        return
      }

      if (payData.payUrl || payData.payDataType === 'payurl') {
        location.href = payData.payUrl || payData.payData
        return
      }

      if (payData.formContent || payData.payDataType === 'form') {
        this.submitForm(payData.formContent || payData.payData)
        return
      }

      if (payData.codeImgUrl || payData.payDataType === 'codeImgUrl') {
        this.qrPayData = payData
        return
      }

      alert('支付通道未返回可用的支付参数')
    },

    handlePaySuccess() {
      finishSuccess(this.payOrderInfo.returnUrl)
    },

    submitForm(formContent) {
      const wrap = document.createElement('div')
      wrap.style.display = 'none'
      wrap.innerHTML = formContent
      document.body.appendChild(wrap)
      const form = wrap.querySelector('form')
      if (form) {
        form.submit()
        return
      }
      document.open()
      document.write(formContent)
      document.close()
    }
  }
}
</script>

<style lang="css" scoped>
.web-cashier-page {
  min-height: 100vh;
  background: #f5f6f8;
  color: #1f2733;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  -webkit-text-size-adjust: 100%;
}

.web-main {
  padding: 42px 34px 430px;
}

.merchant-card {
  padding: 48px 44px 36px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 18px 46px rgba(21, 52, 96, 0.07);
}

.merchant-row {
  display: flex;
  align-items: center;
}

.merchant-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 78px;
  height: 78px;
  border-radius: 22px;
  background: #edf4ff;
}

.merchant-avatar img {
  width: 46px;
  height: 46px;
}

.merchant-meta {
  min-width: 0;
  margin-left: 22px;
}

.pay-title {
  color: #7a8494;
  font-size: 24px;
}

.merchant-name {
  margin-top: 8px;
  color: #1f2733;
  font-size: 30px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.amount-box {
  display: flex;
  align-items: baseline;
  min-height: 118px;
  margin-top: 52px;
  border-bottom: 1px solid #edf0f5;
}

.currency {
  margin-right: 14px;
  font-size: 48px;
  font-weight: 700;
}

.amount-text {
  min-width: 0;
  color: #151b26;
  font-size: 72px;
  font-weight: 700;
  word-break: break-all;
}

.amount-text.empty {
  color: #b5bdc9;
  font-size: 42px;
  font-weight: 500;
}

.order-summary {
  margin-top: 24px;
  padding: 18px 0 0;
  border-top: 1px solid #edf0f5;
}

.order-row {
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
  padding: 8px 0;
}

.order-label {
  color: #748094;
  font-size: 23px;
  line-height: 1.5;
  white-space: nowrap;
}

.order-value {
  color: #1f2733;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.5;
  text-align: right;
  word-break: break-word;
}

.pay-way-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px;
  margin-top: 32px;
}

.pay-brand-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 96px;
  padding: 10px 8px;
  border: 1px solid transparent;
  border-radius: 16px;
  background: #fff;
  color: #475569;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.pay-brand-button:active {
  transform: scale(0.98);
}

.brand-logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
}

.brand-logo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.brand-label {
  display: block;
  width: 100%;
  margin-top: 7px;
  overflow: hidden;
  color: #475569;
  font-size: 18px;
  line-height: 1.2;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pay-brand-button.wechat.active {
  border-color: #18b466;
  background: #eefbf4;
  box-shadow: 0 8px 18px rgba(24, 180, 102, 0.12);
}

.pay-brand-button.wechat.active .brand-label {
  color: #10864d;
  font-weight: 700;
}

.pay-brand-button.alipay.active {
  border-color: #2f80ed;
  background: #edf5ff;
  box-shadow: 0 8px 18px rgba(47, 128, 237, 0.12);
}

.pay-brand-button.alipay.active .brand-label {
  color: #1f6fd1;
  font-weight: 700;
}

.pay-brand-button.unionpay.active {
  border-color: #d95661;
  background: #fff1f2;
  box-shadow: 0 8px 18px rgba(217, 86, 97, 0.12);
}

.pay-brand-button.unionpay.active .brand-label {
  color: #b8323f;
  font-weight: 700;
}

.pay-brand-button.exclusive.active {
  border-color: #3a6ff8;
  background: #eef4ff;
  box-shadow: 0 8px 18px rgba(58, 111, 248, 0.12);
}

.pay-brand-button.exclusive.active .brand-label {
  color: #2458d8;
  font-weight: 700;
}

.product-list {
  margin-top: 24px;
}

.product-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 126px;
  margin-bottom: 16px;
  padding: 18px;
  border: 2px solid transparent;
  border-radius: 18px;
  background: #fff;
  text-align: left;
  box-shadow: 0 12px 32px rgba(21, 52, 96, 0.06);
}

.product-item.active {
  border-color: #2f80ed;
  background: #f2f7ff;
}

.product-cover {
  width: 88px;
  height: 88px;
  border-radius: 16px;
  overflow: hidden;
  background: #eef2f7;
  flex: 0 0 auto;
}

.product-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-body {
  min-width: 0;
  margin-left: 18px;
}

.product-name {
  display: flex;
  align-items: center;
  min-width: 0;
  color: #1f2733;
  font-size: 28px;
  font-weight: 650;
}

.product-name span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-name i {
  margin-left: 12px;
  padding: 4px 8px;
  border-radius: 8px;
  background: #ffecec;
  color: #d95151;
  font-size: 18px;
  font-style: normal;
  flex: 0 0 auto;
}

.product-name i.custom {
  background: #e8f7ef;
  color: #15945f;
}

.product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 12px;
  color: #6b7480;
  font-size: 22px;
}

.empty-products {
  padding: 28px 0;
  color: #7a8494;
  font-size: 24px;
  text-align: center;
}

.number-pad,
.fixed-pay-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 18px 20px 26px;
  background: #fff;
  box-shadow: 0 -14px 34px rgba(15, 23, 42, 0.08);
}

.key-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.num-key {
  height: 82px;
  border: 0;
  border-radius: 12px;
  background: #f2f4f7;
  color: #172033;
  font-size: 34px;
  font-weight: 600;
}

.num-key:active {
  transform: scale(0.98);
  background: #e6ebf2;
}

.num-key.muted {
  color: #687386;
}

.pay-button {
  width: 100%;
  height: 84px;
  margin-top: 16px;
  border: 0;
  border-radius: 14px;
  background: #2f80ed;
  color: #fff;
  font-size: 30px;
  font-weight: 700;
}

.pay-button:disabled {
  background: #c7d3e4;
}

.qr-result {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 42px;
  box-sizing: border-box;
}

.qr-card {
  width: 100%;
  max-width: 560px;
  padding: 46px 36px;
  box-sizing: border-box;
  border-radius: 22px;
  background: #fff;
  text-align: center;
  box-shadow: 0 18px 46px rgba(21, 52, 96, 0.07);
}

.qr-card h1 {
  margin: 0 0 28px;
  font-size: 34px;
}

.qr-card img {
  width: 360px;
  max-width: 100%;
  height: auto;
}

.qr-card p {
  margin: 26px 0 0;
  color: #657083;
  font-size: 26px;
}
</style>
